import { useState, useRef, useEffect } from 'react'
import { LogOut, Settings, ChevronUp, Shield, User, TextSelect } from 'lucide-react'

interface UserMenuProps {
  user: {
    name: string
    email?: string
    avatarUrl?: string
    admin?: boolean
  }
  onUserSettings?: () => void
  onSettings?: () => void
  onGemTemplates?: () => void
  onLogout?: () => void
}

export function UserMenu({ user, onUserSettings, onSettings, onGemTemplates, onLogout }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Get initials for avatar fallback
  const initials = user.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <div ref={menuRef} className="relative px-3 py-3">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm
          text-stone-700 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-800
          transition-colors duration-150"
      >
        {/* Avatar */}
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.name}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-teal-100 border border-teal-300 dark:bg-teal-900 dark:border-teal-700 flex items-center justify-center">
            <span className="text-xs font-medium text-teal-700 dark:text-teal-300">
              {initials}
            </span>
          </div>
        )}

        {/* Name & Email */}
        <div className="flex-1 text-left overflow-hidden">
          <div className="font-medium truncate">{user.name}</div>
          {user.email && (
            <div className="text-xs text-stone-500 dark:text-stone-500 truncate">
              {user.email}
            </div>
          )}
        </div>

        {/* Chevron */}
        <ChevronUp
          className={`w-4 h-4 text-stone-400 transition-transform duration-150 ${
            isOpen ? '' : 'rotate-180'
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute bottom-full left-3 right-3 mb-1 py-1 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700">
          {user.admin && (
            <a
              href="/admin"
              onClick={() => setIsOpen(false)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300
                hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors duration-150"
            >
              <Shield className="w-4 h-4" />
              <span>Admin</span>
            </a>
          )}
          <button
            onClick={() => {
              setIsOpen(false)
              onUserSettings?.()
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300
              hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors duration-150"
          >
            <User className="w-4 h-4" />
            <span>User Settings</span>
          </button>
          <button
            onClick={() => {
              setIsOpen(false)
              onSettings?.()
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300
              hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors duration-150"
          >
            <Settings className="w-4 h-4" />
            <span>AI Settings</span>
          </button>
          <button
            onClick={() => {
              setIsOpen(false)
              onGemTemplates?.()
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300
              hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors duration-150"
          >
            <TextSelect className="w-4 h-4" />
            <span>Gem Templates</span>
          </button>
          <button
            onClick={() => {
              setIsOpen(false)
              onLogout?.()
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300
              hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors duration-150"
          >
            <LogOut className="w-4 h-4" />
            <span>Log out</span>
          </button>
        </div>
      )}
    </div>
  )
}
